import React, { ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import {
  AdvancedCobranding,
  SimpleCobranding,
  useCobranding,
} from '../../utilities/cobranding/cobranding';
import { useAdvancedCobrandingContext } from './AdvancedCobrandingContext';
import Spinner from '@atlaskit/spinner';
import { captureTaggedException } from '../../utilities/analytics/error-reporting';

export type PageGuardProps = {
  /** The children to render. This is usually the cobranded page or the non-cobranded/default/base page. */
  children: ReactNode;
  /** The required components that the page needs to register. This is used to determine what components the page needs to have registered before it can be rendered. */
  requiredComponents: Array<string>;
  /** Whether or not the registration has started. This is used to determine if we should show the loading overlay. */
  registrationStarted: boolean;
  /** Whether or not to skip the checks for required components. This is used when the page is using a fallback and we do not want to enforce the required components.. */
  skipRequiredChecks: boolean;
};

/**
 * Temporary solution to determine how long the PageGuard will give the components to register themselves.
 * We want to give cobranding time to load but no matter what we want to have a graceful fallback
 * This makes testing more complicated, therefore this constant exists.
 */
export const REGISTRATION_TIMEOUT_MS = 2000;

/**
 * Whether or not to show the missing component error overlay. We do not want to show this in production (to real users).
 * The overlay is for developers to see the missing components and debug the issue
 */
const canShowOverlay = process.env.NODE_ENV !== 'production';

export const PageGuard = ({
  children,
  requiredComponents,
  registrationStarted,
  skipRequiredChecks = false,
}: PageGuardProps) => {
  const { cobranding } = useCobranding();
  const { components } = useAdvancedCobrandingContext();
  const [registrationTimedOut, setRegistrationTimedOut] = useState(false);

  const missingComponents = useMemo(() => {
    return requiredComponents.filter(requiredComponent => !components?.includes(requiredComponent));
  }, [requiredComponents, components]);

  useEffect(() => {
    if (!registrationStarted) {
      return;
    }

    const timeout = setTimeout(() => {
      setRegistrationTimedOut(true);
    }, REGISTRATION_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [registrationStarted]);

  const hasRegisteredAllRequiredComponents = missingComponents.length === 0;
  const isWaitingForRegistration = !registrationTimedOut;

  useEffect(() => {
    if (!hasRegisteredAllRequiredComponents && !isWaitingForRegistration && !canShowOverlay) {
      console.warn(
        `[COBRANDING] ${window.location.pathname} has not registered all required components.`
      );
    }
  }, [cobranding, hasRegisteredAllRequiredComponents, isWaitingForRegistration]);

  // skipRequiredChecks:
  // We want to skip these checks if we are using a fallback (i.e. a base page/non cobranded page) and we do not want to
  // enforce the required components. This is at least until we either audit all of the fallback pages, or we start
  // dogfooding the cobranding orchestrator in our base pages, to ensure that we are not breaking any pages.
  // hasRegisteredAllRequiredComponents:
  // If all required components have been registered, we can safely render the children without any further checks.
  if (skipRequiredChecks || hasRegisteredAllRequiredComponents) {
    return (
      <Suspense fallback={<LoadingOverlay />}>
        <div
          style={{ height: '100%' }} // important for children elements to flex-grow and fill the screen properly
          data-testid="registration-complete">
          {children}
        </div>
      </Suspense>
    );
  }

  if (isWaitingForRegistration) {
    // Displays the loading overlay while the page is lazy-loading and while the registration is in progress.
    return (
      <Suspense fallback={<LoadingOverlay />}>
        {children}
        <LoadingOverlay />
      </Suspense>
    );
  }

  // This handles the case where the registration has timed out and the required components are still not registered
  return (
    <PageGuardMissingComponent
      canShowOverlay={canShowOverlay}
      missingComponents={missingComponents}>
      {children}
    </PageGuardMissingComponent>
  );
};

type PageGuardMissingComponentProps = {
  /** The component to render */
  children: React.ReactNode;
  /** Whether or not we should render the error overlay (e.g. when running on local dev) */
  canShowOverlay: boolean;
  /** The components that the component is missing (the check times out after REGISTRATION_TIMEOUT_MS) based on the required components declared by the page cobranding config */
  missingComponents: string[];
};

/**
 * Responsible for rendering the children component (which is either the cobranded page or the fallback/non-cobranded/default/base page)
 * and checking that the page includes the components that the relevant page level cobranded config declares that it needs
 * When running locally, it also renders details of the missing components to let the developers know as well as aid with debugging the issue.
 * In production we do not show this to the user.
 * In all cases, we render the children despite it missing some details as a fallback.
 *
 * @note We send an event to Sentry in the case that this component is rendered, because we should _not_ see this in customer facing code.
 */
const PageGuardMissingComponent = ({
  children,
  canShowOverlay,
  missingComponents,
}: PageGuardMissingComponentProps) => {
  const { cobranding } = useCobranding();

  // Generating here so that cobranding does not become a dependency of the useEffect. errorMessage will be, but it won't run again unless the string itself has changed.
  const errorMessage = `Cobranding experience missing required components after registration waiting period ${cobranding}`;

  useEffect(() => {
    // If we get to this point, it means that after waiting for REGISTRATION_TIMEOUT_MS the required components are still not present
    captureTaggedException(new Error(errorMessage), {
      capabilities: ['non-kirby-code'],
      tags: {
        cobrandingOrchestrator: 'missing_required_components',
      },
    });
  }, [errorMessage]);

  if (canShowOverlay) {
    return (
      <div data-testid="registration-complete">
        <MissingComponentErrorDisplay cobranding={cobranding} missingComponents={missingComponents}>
          {children}
        </MissingComponentErrorDisplay>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingOverlay />}>
      <div
        style={{ height: '100%' }} // important for children elements to flex-grow and fill the screen properly
        data-testid="registration-complete">
        {children}
      </div>
    </Suspense>
  );
};

const MissingComponentErrorDisplay = ({
  cobranding,
  missingComponents,
  children,
}: {
  cobranding: SimpleCobranding | AdvancedCobranding;
  missingComponents: string[];
  children: ReactNode;
}) => {
  const search = new URLSearchParams(window.location.search);
  search.delete('token');

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'fixed',
          zIndex: 100,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <div
          style={{
            position: 'fixed',
            zIndex: -1,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.25,
            backgroundColor: '#FFADB0',
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            padding: '4rem',
            borderRadius: '0.5rem',
            background: '#FFADB0',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow:
              'rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px',
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <h2 style={{ fontWeight: 'bold' }}>Advanced Cobranding</h2>
            <p>Unable to render page due to missing required components:</p>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.1rem',
              padding: '1rem',
              borderRadius: '0.25rem',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#c92216',
            }}>
            {missingComponents.map(component => (
              <p style={{ margin: 0 }} key={component}>
                <span data-testid="missing-component">{component}</span>
              </p>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
            }}>
            <p style={{ margin: 0 }}>
              <strong>Cobranding</strong>: {cobranding}
            </p>
            <p style={{ margin: 0, display: 'flex', flexWrap: 'wrap' }}>
              <strong>URL</strong>: {`${window.location.pathname}?${search}`}
            </p>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

const LoadingOverlay = () => (
  <div
    style={{
      position: 'fixed',
      top: 0,
      zIndex: 2,
      pointerEvents: 'none',
      left: 0,
      right: 0,
      bottom: 0,
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'white',
    }}>
    <Spinner />
  </div>
);
